/**
 * UK mobile number validation + normalisation.
 *
 * Accepts a variety of user inputs:
 *   07441 916 737, 07441916737, 7441916737,
 *   +447441916737, 00447441916737, 447441916737
 * Spaces, dashes, brackets and dots are stripped.
 *
 * Returns the normalised UK local format `07XXXXXXXXX` (11 digits)
 * or `null` if the input is not a valid UK mobile number.
 *
 * Validation rule: UK mobile numbers are 11 digits starting with `07`
 * (E.164: +44 7XXX XXX XXX, total 12 chars incl. `+`).
 */
export function normaliseUKMobile(input: string): string | null {
  if (!input) return null;

  // Strip spaces, dashes, brackets, dots, slashes
  let cleaned = input.replace(/[\s\-\(\)\.\/]/g, "");

  // Drop leading +
  if (cleaned.startsWith("+")) cleaned = cleaned.slice(1);
  // Drop international access prefix 00
  if (cleaned.startsWith("00")) cleaned = cleaned.slice(2);
  // Strip country code 44
  if (cleaned.startsWith("44")) cleaned = cleaned.slice(2);
  // Strip national trunk 0
  if (cleaned.startsWith("0")) cleaned = cleaned.slice(1);

  // Must be digits only at this point
  if (!/^\d+$/.test(cleaned)) return null;

  // UK mobile: 10 digits starting with 7 (e.g. 7441916737)
  if (cleaned.length !== 10 || !cleaned.startsWith("7")) return null;

  return "0" + cleaned;
}

export function isValidUKMobile(input: string): boolean {
  return normaliseUKMobile(input) !== null;
}

const EMAIL_REGEX = /^[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9])?@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

export function isValidEmail(input: string): boolean {
  if (!input) return false;
  const trimmed = input.trim();
  if (trimmed.length > 254) return false;
  const atIdx = trimmed.indexOf("@");
  if (atIdx < 0) return false;
  const local = trimmed.slice(0, atIdx);
  if (!local || local.length > 64) return false;
  if (local.includes("..")) return false;
  return EMAIL_REGEX.test(trimmed);
}

export function getPhoneError(input: string): string | null {
  const trimmed = input?.trim();
  if (!trimmed) return null;
  if (!isValidUKMobile(trimmed)) return "Enter a valid UK mobile number";
  return null;
}

export function getEmailError(input: string): string | null {
  const trimmed = input?.trim();
  if (!trimmed) return null;

  if (trimmed.length > 254) return "Email address is too long";
  const atIdx = trimmed.indexOf("@");
  if (atIdx < 0) return "Email must contain an @ symbol";
  const local = trimmed.slice(0, atIdx);
  if (!local || local.length > 64) return "The part before @ is too long";
  if (local.includes("..")) return "Email cannot contain consecutive dots (..)";
  if (!EMAIL_REGEX.test(trimmed)) return "Enter a valid email address";
  return null;
}
